/**
 * Core UI copy, English. These are the source strings: every other core
 * catalog (see `./locales/`) and every brand override (`config.copy`,
 * `config.i18n[locale].copy`) is keyed by these ids. `{name}` style
 * placeholders are substituted by `makeCopy`.
 */
export const copyDefaults = {
  "nav.catalog": "Designs",
  "nav.mine": "My designs",
  "catalog.title": "Pick a design",
  "catalog.empty": "No designs published yet.",
  "configurator.params": "Parameters",
  "configurator.presets": "Presets",
  "configurator.reset": "Reset",
  "configurator.export.stl": "Download STL",
  "configurator.export.3mf": "Download 3MF",
  "configurator.save": "Save",
  "configurator.savedProfile": "Saved to My designs.",
  "configurator.savedView": "View",
  "configurator.savedDevice": "Saved on this device. Sign in to keep it everywhere.",
  "configurator.share": "Copy link",
  "configurator.update": "Update to {version}",
  "configurator.building": "Building…",
  "configurator.warnings": "Print check",
  "mine.title": "My designs",
  "mine.empty": "Nothing saved yet. Configure a design and press Save.",
  "auth.signIn": "Sign in",
  "auth.signOut": "Sign out",
  "auth.required": "Sign in to save designs to your profile.",
  /** Export button label for signed-out visitors when `features.exportRequiresSignIn` is on. */
  "auth.signInToExport": "Sign in to download",
  "footer.madeWith": "Geometry is generated in your browser; nothing is uploaded until you save.",
  "fits.invalid": "That doesn't look like a valid reference.",
  "fits.max": "Up to {max} tags.",
  "fits.printed": "I've printed this and it fits",
  "community.title": "Community configurations",
  /** Accessible name of the report reason select. */
  "community.reportReason": "Reason",
  "community.reportDetails": "Details (optional)",
  "share.missing": "This link has expired or does not exist",
  "share.missingBody": "Ask for a new link, or start from a design.",
  "share.browse": "Browse designs",
  "catalog.eyebrow": "",
  "mine.delete": "Delete",
  "mine.deleteConfirm": "Delete \"{name}\"? This can't be undone.",
  "mine.onDevice": "On this device",
  "mine.cancel": "Cancel",
  "mine.rename": "Rename",
  "mine.renameLabel": "Rename {name}",
  "mine.nameField": "Name",
  "mine.renameSave": "Save name",
  "mine.renameHint": "Leave it empty to name it by its dimensions.",
  /** Tooltip on a saved bracket's name: the parameter code the part is engraved with. */
  "mine.codeHint": "Code: {code}",
  "community.empty": "Nothing tagged here yet.",
  "community.confirmed": "Print registered. Thanks!",
  "community.report": "Report",
  "community.reported": "Reported. Thanks, we'll take a look.",
  "community.publishFitLabel": "Which {label} does it fit?",
  "community.cancel": "Cancel",
  "community.viewInCommunity": "See it in the community",
  "community.tag": "Tag a {label} it fits…",
  "community.tagTitle": "Tag it for the community",
  "community.tagHint": "Tell others which thing these parameters fit. Your name shows next to it; you can remove it any time.",
  "community.tagSubmit": "Tag",
  "community.tagNeedsFit": "Pick or type a {label} first.",
  "community.tagNeedsName": "Not tagged yet: it needs your public name. Your set is still here; press Tag to try again.",
  "community.tagged": "Tagged {refs}.",
  "community.tagAnother": "Tag another {label}",
  "community.alreadyTagged": "Already in the community for {refs}.",
  "community.taggedBy": "by {name}",
  "community.taggers": "Tagged by {names}",
  "community.yourTag": "your tag",
  "community.removeTag": "Remove your tag {ref}",
  /* ---- community panel (under the 3D view: this design's and its family's published configurations) ---- */
  /** Configurator tab next to the parameters. */
  "community.tab": "Community",
  "community.count": "{count} tagged",
  "community.countOne": "1 tagged",
  /** Under the generated name in the save panel. */
  "configurator.nameHint": "Named after its parameters.",
  "configurator.renamed": "Renamed to {name}",
  /** "Start from a set" above the parameters; `{label}` is the brand's fit label ("LEGO® set"). */
  "configurator.startFromSet": "Start from a {label}",
  "configurator.startFromSetUse": "Use its dimensions",
  "configurator.startFromSetHint": "Starts from the dimensions the community tagged for it. Check them against yours before printing.",
  "configurator.startFromSetNone": "Nothing tagged for {ref} here yet.",
  "configurator.startedFromSet": "Started from the dimensions tagged for {ref}.",
  /** Save block: keep what is on the model as the start of a new saved design. */
  "configurator.new": "New design",
  "configurator.newHint": "Start a new design from what's on the model. The saved one stays as it is.",
  "configurator.startedNew": "Started a new design from these parameters.",
  "community.favorite": "Favorite",
  "community.favorited": "Added to Favorites in My designs.",
  "community.addFit": "Add a {label} it fits",
  "community.confirmFitLabel": "I printed it for {ref}",
  "community.fitUnconfirmed": "No prints yet",
  /** Prints registered for one fit (pill badge and fit list). */
  "community.fitPrints.one": "1 print",
  "community.fitPrints.other": "{count} prints",
  /** Everyone who printed this parameter combination, for any fit (author included when they marked it printed). */
  "community.printers.one": "Printed by 1 person",
  "community.printers.other": "Printed by {count} people",
  "community.alreadyRegistered": "You already registered this print.",
  /** Checkbox next to "add another fit": also register the user's print on what they add. */
  "community.addFitPrinted": "I printed it for this",
  "community.addFitSubmit": "Add",
  "community.addFitNeedsRef": "Type a {label} first.",
  "community.addedFit": "You added {refs}. Thanks!",
  "community.alreadyHasFit": "It already lists {refs}.",
  "mine.liked": "Favorites",
  "mine.yours": "Yours",
  "mine.tagged": "Your tags",
  "mine.taggedEmpty": "Nothing tagged yet. Tag what a design fits from its Parameters tab.",
  "mine.favoritesEmpty": "No favorites yet. Use ☆ Favorite on a community design.",
  /** Accessible name of the community list's search field (placeholder too). */
  "community.searchLabel": "Search by reference, name or person",
  "community.sortLabel": "Sort",
  "community.sort.newest": "Newest",
  "community.sort.downloads": "Most downloaded",
  "community.sort.confirmed": "Most printed",
  "community.sort.likes": "Most saved",
  "community.sort.name": "Name",
  "community.noResults": "Nothing matches \u201c{query}\u201d.",
  "community.emptyHint": "Tag what a design fits from the Parameters tab to list it here.",
  "community.restore": "Back to mine",
  "community.locked": "A community design is on the model, so the parameters are locked.",
  "community.reportTag": "Report {ref}",
  "community.confirmFitShort": "I printed it",
  "community.setsLabel": "What it fits",
  "community.onModelLabel": "Community design on the model",
  "community.sets.other": "{count} tags",
  "community.sets.one": "1 tag",
  "community.youPrinted": "✓ You printed it",
  /** Entry made for a family sibling (same params, other mount); `{design}` is its title. */
  "community.madeFor": "Made for {design}",
  "community.copyLink": "Copy link",
  "community.linkCopied": "Link copied",
  /** Unique-user download counts. */
  "downloads.one": "1 download",
  "downloads.other": "{count} downloads",
  /* ---- design families (same params, other variant) ---- */
  "family.label": "Variant",
  "family.hint": "Your parameters carry over.",
  /* ---- fit tag input ---- */
  "fits.hint": "Type a reference and press Enter, or pick a suggestion.",
  "fits.suggestions": "Suggestions",
  /** While an external catalog (fitTags.lookup) is being searched. */
  "fits.searching": "Searching…",
  "profile.displayName": "Display name",
  "profile.dialogTitle": "Choose your public name",
  "profile.prompt": "It's shown next to the sets you tag. Your email and Google name never are.",
  "profile.save": "Use this name",
  "mod.title": "Moderation queue",
  "mod.empty": "No reported entries.",
  /* ---- shared ---- */
  "common.loading": "Loading…",
  "common.retry": "Retry",
  /** Accessible name of the × on a status message. */
  "common.dismiss": "Dismiss",
  "common.noApi": "This deployment has no API configured.",
  /* ---- header / navigation / language ---- */
  /** Accessible name of the primary navigation. */
  "nav.primaryLabel": "Primary",
  /** Skip link at the top of the page. */
  "a11y.skipToContent": "Skip to content",
  /** Accessible name of the language switcher. */
  "locale.label": "Language",
  /** Fallback account name when the user has neither a name nor an email. */
  "auth.account": "Account",
  /** Tooltip on the account button. */
  "auth.signedInAs": "Signed in as {email}",
  /* ---- catalog ---- */
  "catalog.loading": "Loading designs…",
  "catalog.error": "Could not load the catalog",
  /** Developer hint under an empty catalog. */
  "catalog.emptyBody": "Run \"pnpm design build packs/<pack>\" to publish designs locally.",
  /** Catalog card call to action (design cards / community cards). */
  "card.configure": "Configure",
  "card.view": "View",
  /** Accessible name of a card's fit list; `{label}` is the brand's fit noun. */
  "card.fitsLabel": "Fits {label}",
  "card.fits": "Fits",
  /* ---- configurator ---- */
  "configurator.preparing": "Preparing…",
  "configurator.exporting": "Exporting…",
  "configurator.saving": "Saving…",
  "configurator.done.export": "✓ Downloaded",
  "configurator.done.save": "✓ Saved",
  "configurator.done.share": "✓ Link copied",
  "configurator.saveHeading": "My design",
  "configurator.viewport": "3D preview of {title}",
  "configurator.busy": "building…",
  /** Clamp hint under a number field; `{value}` is the bound with its unit ("50 mm"). */
  "configurator.clampedMin": "Min {value}",
  "configurator.clampedMax": "Max {value}",
  /** After clamping; `{input}` is what was typed. Stays until the next edit. */
  "configurator.clampedToMin": "{input} is below the minimum, set to {value}",
  "configurator.clampedToMax": "{input} is above the maximum, set to {value}",
  /** Under a focused number field; `{max}` carries the unit ("Range 5–50 mm"). */
  "configurator.range": "Range {min}–{max}",
  "configurator.loading": "Loading design…",
  "configurator.live.exported": "Downloaded {filename} ({size})",
  "configurator.live.linkCopied": "Link copied: {url}",
  "configurator.live.linkReady": "Copy this link: {url}",
  "configurator.openError": "Could not open this design",
  "configurator.notFound": "Design not found",
  "configurator.notFoundBody": "{design} is not in this catalog.",
  /** Tooltip on the version pill. */
  "configurator.versionHint": "Design version pinned for this configuration",
  "configurator.deprecated": "deprecated",
  "configurator.printNotes": "Print notes.",
  /** Mesh telemetry under the viewport. */
  "configurator.telemetry": "{tris} tris · {ms} ms",
  /** Title of the parameter group holding params no declared group lists (when other groups exist). */
  "configurator.groupGeneral": "General",
  /* ---- 3D viewport ---- */
  "viewport.role": "3D viewport",
  "viewport.label": "3D preview",
  /** One-time chip over the 3D view until the first orbit. */
  "viewport.orbitHint": "Drag to rotate · scroll to zoom",
  "viewport.orbitHintTouch": "Drag to rotate · pinch to zoom",
  "viewport.orbitHintDismiss": "Dismiss",
  /** Switch over the 3D view: how many dimension lines (or labels) to show; the selected field's always shows. */
  "viewport.dimensions": "Dimensions",
  "viewport.dimensionsNone": "None",
  "viewport.dimensionsAll": "All",
  /* ---- fit tags ---- */
  "fits.nameLabel": "Name",
  /* ---- community / share / profile / moderation ---- */
  "community.error": "Could not load community configurations",
  /** Report reasons (API values wrong-fit, spam, offensive, other). */
  "community.reason.wrong-fit": "Wrong fit",
  "community.reason.spam": "Spam",
  "community.reason.offensive": "Offensive",
  "community.reason.other": "Other",
  "share.noApi": "Sharing needs the API",
  "share.opening": "Opening shared design…",
  "profile.change": "change",
  "profile.invalid": "3–30 letters, digits, _ . -",
  "mine.deleteLabel": "Delete {name}",
  "mod.staffOnly": "Staff only",
  "mod.removeConfirm": "Remove this tag? Prints registered for it go with it.",
  "mod.reports": "{count} report(s) since {date}",
  "mod.view": "view",
  "mod.hide": "Hide",
  "mod.restore": "Restore",
  "mod.dismiss": "Dismiss",
  "mod.dismissHint": "Clear the reports and keep it live",
  "mod.remove": "Remove",
  "mod.status.live": "live",
  "mod.status.hidden": "hidden",
  /* Home page set search (brand `fitTags`). */
  "home.findLabel": "Find what fits yours",
  "home.findPlaceholder": "Type a reference or a name",
  "home.findSubmit": "Find",
  "home.clear": "Clear",
  "home.resultsTitle": "Designs tagged for {ref}",
  "home.entries.one": "1 community version for it",
  "home.entries.other": "{count} community versions for it",
  "home.noneTitle": "Nothing tagged for {ref} yet",
  "home.noneBody": "Open a design below, adjust it until it fits, and tag it with {ref} from the Parameters tab so the next person finds it here.",
  "home.error": "Could not search the community",
  "home.allTitle": "All designs",
  "home.noMatch": "No tagged set matches “{query}”. Type its reference number, or size a design to it below and tag it.",
  "home.noMatchCustomize": "Customize your own ↓",
  "home.invalid": "We couldn't find that. Try its reference number, or customize your own below.",
  /** Second half of the home hero heading, after `home.findLabel`; jumps to the catalog. */
  "home.orCustomize": "or customize your own",
  "community.fitFilter": "Only versions tagged for",
  "community.fitFilterClear": "Show all",
  "community.fitNone": "Nothing in this design is tagged for {ref} yet.",
  /* After a download: "Next steps" card and the "did it fit?" reminder. */
  "next.title": "Next steps",
  "next.downloaded": "Downloaded {filename} ({size})",
  "next.printTitle": "Print it",
  "next.tip.orientation": "Orientation",
  "next.tip.supports": "Supports",
  "next.tip.supportsYes": "Needed",
  "next.tip.supportsNo": "None",
  "next.tip.layerHeight": "Layer height",
  "next.tip.infill": "Infill",
  "next.tip.walls": "Walls",
  "next.tip.material": "Material",
  "next.tip.hardware": "You'll need",
  "next.filament": "At most {grams} g of PLA ({volume} cm³ if printed solid).",
  "next.printedTitle": "Printed it?",
  "next.printedHint": "Tell others it fits, so the next person can trust these dimensions.",
  "next.printedFor": "I printed it for {ref}",
  "next.printedTag": "I printed it: tag what it fits",
  "next.printedPick": "Which one did you print it for?",
  "next.printedDone": "✓ Your print for {ref} is registered. Thanks!",
  "next.signIn": "Sign in to say you printed it",
  "next.save": "Save it, to tweak it after a test print",
  "next.reminder": "You downloaded {name} on {date}. Did it fit?",
  "next.reminderYes": "Yes, I printed it",
  "next.reminderNo": "Not quite, adjust it",
  "next.adjusting": "Your downloaded dimensions are on the model: adjust them and download again.",
  /* Error states, community "start from this one", print counts on home results. */
  "error.offline": "We can't reach the server. Check your connection and try again.",
  "error.signIn": "Sign in to open this.",
  "error.notFound": "It isn't here any more, or the link is wrong.",
  "error.generic": "Something went wrong on our side. Try again in a moment.",
  "community.adopt": "Start from this one",
  "community.adopted": "These dimensions are yours now: change them, save or tag them. The community's entry stays as it is.",
  "home.prints.one": "✓ 1 print for this set",
  "home.prints.other": "✓ {count} prints for this set",
  /* Footer links, tag removal (undo/confirm), panel jump. */
  "footer.privacy": "Privacy",
  "footer.terms": "Terms",
  "footer.contact": "Contact",
  "community.tagRemoved": "Removed your tag {ref}.",
  "community.tagRestored": "{ref} is your tag again.",
  "community.undo": "Undo",
  "community.removeLast": "{ref} is this entry's only set: removing your tag removes the entry from the community, prints included.",
  "community.removePrinted.one": "1 other person registered a print for {ref}; removing your tag removes it too.",
  "community.removePrinted.other": "{count} other people registered prints for {ref}; removing your tag removes them too.",
  "community.removeLastYes": "Remove",
  "community.removeLastNo": "Keep it",
  "community.reportHint": "A few reports hide the tag until a moderator reviews it.",
  "community.appliedLive": "Community version on the model.",
  "configurator.saveJumpHint": "Save, copy link, tag",
  /* File format hints, favorites removal. */
  "configurator.export.stlHint": "STL: works in every slicer; millimetres.",
  "configurator.export.3mfHint": "3MF: keeps units and the part's name; best for PrusaSlicer, Bambu Studio and Cura.",
  "configurator.export.formatHint": "STL works everywhere; 3MF keeps units and suits PrusaSlicer, Bambu Studio and Cura.",
  "community.unfavorited": "Removed from Favorites.",
  "mine.unfavorite": "Remove",
  "mine.unfavoriteLabel": "Remove {name} from Favorites",
  /* Home: how it works, cost, community proof. */
  "home.howLabel": "How it works",
  "home.step1": "Find what yours is",
  "home.step2": "Pick a version people printed",
  "home.step3": "Download & print",
  "home.free": "Free STL and 3MF files",
  "home.freeSignIn": "Free STL and 3MF files · sign in with Google to download",
  "home.proof": "{count} references already tagged by the community",
} as const;
export type CopyKey = keyof typeof copyDefaults;
